import { ArrowRight, Check } from 'lucide-react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
    Text,
} from '@/ui/design-system/components';
import {
    CATEGORY_INSTRUCTIONS_PLACEHOLDER,
    CATEGORY_INSTRUCTION_EXAMPLES,
} from './contextConstants';
import { useEffect, useMemo, useState } from 'react';
import { Textarea } from '@/ui/design-system/components/ui/textarea';
import { cn } from '@/ui/design-system/utils/cn';
import toast from 'react-hot-toast';
import { useCreateAlertContext } from '@/utils/hooks/alerts';

const CONTEXT_TYPES = [
    {
        value: 'brand',
        title: 'Brand',
        description: 'Company, label, or product line',
    },
    {
        value: 'category',
        title: 'Category',
        description: 'Topic, ingredient, or trend data',
    },
];

export default function ContextEmptyStateModal({
    open,
    onOpenChange,
    onCreated,
}) {
    const createContext = useCreateAlertContext();
    const [step, setStep] = useState(1);
    const [contextType, setContextType] = useState('brand');
    const [name, setName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [instructions, setInstructions] = useState('');

    useEffect(() => {
        if (!open) {
            setStep(1);
            setContextType('brand');
            setName('');
            setWebsiteUrl('');
            setInstructions('');
        }
    }, [open]);

    const canContinue = useMemo(() => Boolean(name.trim()), [name]);
    const canSave = useMemo(() => {
        if (!name.trim()) {
            return false;
        }
        if (contextType === 'brand') {
            return Boolean(websiteUrl.trim());
        }
        return Boolean(instructions.trim());
    }, [name, contextType, websiteUrl, instructions]);

    const handleContinue = () => {
        if (!canContinue) {
            return;
        }
        setStep(2);
    };

    const handleSave = async () => {
        if (!canSave) {
            return;
        }
        try {
            const created = await createContext.mutateAsync({
                name: name.trim(),
                context_type: contextType,
                website_url: contextType === 'brand' ? websiteUrl.trim() : null,
                instructions:
                    contextType === 'category' ? instructions.trim() : null,
            });
            toast.success('Context created. Generating territory...');
            onCreated?.(created);
            onOpenChange(false);
        } catch (error) {
            console.error('Failed to create context:', error);
            toast.error('Failed to create context');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[520px] p-0 gap-0">
                <DialogHeader className="px-4 py-3 border-b">
                    <DialogTitle>New Context</DialogTitle>
                </DialogHeader>

                <div className="px-4 py-4 flex flex-col gap-4">
                    {step === 1 ? (
                        <>
                            <div className="grid grid-cols-2 gap-2">
                                {CONTEXT_TYPES.map((type) => {
                                    const selected = contextType === type.value;
                                    return (
                                        <button
                                            key={type.value}
                                            type="button"
                                            className={cn(
                                                'rounded-md border p-3 text-left transition-colors',
                                                selected
                                                    ? 'border-violet-400 bg-violet-50'
                                                    : 'border-input bg-background hover:bg-muted/40',
                                            )}
                                            onClick={() =>
                                                setContextType(type.value)
                                            }
                                        >
                                            <div className="flex items-center justify-between">
                                                <Text
                                                    as="span"
                                                    className="font-medium"
                                                >
                                                    {type.title}
                                                </Text>
                                                <span
                                                    className={cn(
                                                        'h-4 w-4 rounded-[4px] border flex items-center justify-center',
                                                        selected
                                                            ? 'border-violet-500 bg-violet-500'
                                                            : 'border-input bg-background',
                                                    )}
                                                >
                                                    {selected ? (
                                                        <Check className="h-3 w-3 text-white" />
                                                    ) : null}
                                                </span>
                                            </div>
                                            <Text
                                                as="p"
                                                size="sm"
                                                color="muted"
                                                className="mt-1"
                                            >
                                                {type.description}
                                            </Text>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex flex-col gap-2">
                                <Label htmlFor="empty-context-name">
                                    Context name
                                </Label>
                                <Input
                                    id="empty-context-name"
                                    value={name}
                                    onChange={(event) =>
                                        setName(event.target.value)
                                    }
                                    placeholder="e.g. CeraVe, Retinol Alternatives, K-Beauty..."
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {contextType === 'brand' ? (
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="empty-context-url">
                                        Website URL
                                    </Label>
                                    <Input
                                        id="empty-context-url"
                                        value={websiteUrl}
                                        onChange={(event) =>
                                            setWebsiteUrl(event.target.value)
                                        }
                                        placeholder="https://example.com"
                                    />
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <Label htmlFor="empty-context-instructions">
                                        What are you trying to do?
                                    </Label>
                                    <Textarea
                                        id="empty-context-instructions"
                                        value={instructions}
                                        onChange={(event) =>
                                            setInstructions(event.target.value)
                                        }
                                        className="min-h-[180px]"
                                        placeholder={
                                            CATEGORY_INSTRUCTIONS_PLACEHOLDER
                                        }
                                    />
                                    {!instructions.trim() ? (
                                        <div className="flex flex-col gap-2">
                                            <Text
                                                as="span"
                                                size="sm"
                                                color="muted"
                                            >
                                                Try an example to get started:
                                            </Text>
                                            <div className="flex flex-wrap gap-2">
                                                {CATEGORY_INSTRUCTION_EXAMPLES.map(
                                                    (example) => (
                                                        <Button
                                                            key={example.label}
                                                            type="button"
                                                            variant="outline"
                                                            className="h-9 rounded-md px-3 text-sm"
                                                            onClick={() =>
                                                                setInstructions(
                                                                    example.text,
                                                                )
                                                            }
                                                        >
                                                            {example.label}
                                                        </Button>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter className="w-full px-4 py-3 border-t flex-row justify-between sm:justify-between gap-2 space-x-0 sm:space-x-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <div className="ml-auto flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={step === 1}
                            onClick={() => setStep(1)}
                        >
                            Back
                        </Button>
                        {step === 1 ? (
                            <Button
                                type="button"
                                onClick={handleContinue}
                                disabled={!canContinue}
                            >
                                <span>Continue</span>
                                <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                onClick={handleSave}
                                disabled={!canSave || createContext.isLoading}
                            >
                                {createContext.isLoading ? null : (
                                    <Check className="h-4 w-4 mr-1" />
                                )}
                                {createContext.isLoading
                                    ? 'Saving...'
                                    : 'Save & Add context'}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
